import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Star, BadgeCheck, ArrowRight, Wallet, Building2, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (!u || u.startsWith("http") || u.startsWith("data:") ? u : `${BACKEND_URL}${u}`);
const gbp = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;
const fmtK = (n) => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(n || 0));
const ago = (iso) => {
  if (!iso) return "";
  const d = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : d < 30 ? `${d} days ago` : d < 60 ? "last month" : `${Math.round(d / 30)} months ago`;
};
const PLATFORM = { tiktok: "TikTok", instagram: "Instagram", youtube: "YouTube" };
const HUES = ["from-pink-500 to-purple-600", "from-sky-500 to-indigo-600", "from-amber-400 to-orange-600", "from-emerald-400 to-teal-600", "from-fuchsia-500 to-rose-600", "from-violet-500 to-blue-600"];
const hue = (s) => HUES[[...(s || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % HUES.length];

let cache = null;
export function useResults(limit = 82) {
  const [data, setData] = useState(cache);
  useEffect(() => {
    if (cache && (cache.deals.length >= limit || cache.deals.length >= (cache.totals?.count || 0))) return;
    api.get("/results", { params: { limit } }).then((r) => { cache = r.data; setData(r.data); }).catch(() => setData({ deals: [], totals: {} }));
  }, [limit]);
  return data;
}

function Avatar({ name, url, square = false, to }) {
  const inner = url ? (
    <img src={abs(url)} alt={name} loading="lazy" className={`h-9 w-9 object-cover ${square ? "rounded-lg" : "rounded-full ring-1 ring-white/10"}`} />
  ) : (
    <div className={`h-9 w-9 flex items-center justify-center font-display font-bold text-white text-xs bg-gradient-to-br ${hue(name)} ${square ? "rounded-lg" : "rounded-full"}`}>{(name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").replace(".", "")}</div>
  );
  return to ? <Link to={to} className="shrink-0 hover:opacity-80 transition-opacity" aria-label={name}>{inner}</Link> : inner;
}

function NameLink({ to, children, testid }) {
  return to ? <Link to={to} className="hover:text-nexus-pink transition-colors" data-testid={testid}>{children}</Link> : <span>{children}</span>;
}

function Stars({ rating }) {
  if (!rating) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-300" aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className="relative inline-block h-3 w-3">
          <Star className="h-3 w-3 opacity-25 absolute inset-0" />
          {rating >= i ? <Star className="h-3 w-3 fill-current absolute inset-0" /> : rating >= i - 0.5 ? <span className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}><Star className="h-3 w-3 fill-current" /></span> : null}
        </span>
      ))}
      <span className="text-[10px] text-zinc-400 ml-1">{Number(rating).toFixed(1)}</span>
    </span>
  );
}

export function DealCard({ d, compact = false }) {
  return (
    <article className={`shrink-0 rounded-2xl bg-[#0C0C10] hairline p-4 ${compact ? "w-[280px]" : "w-full"} flex flex-col gap-3`} data-testid={`deal-card-${d.id}`} data-showcase={d.showcase ? "1" : "0"}>
      <div className="flex items-center gap-2">
        <Avatar name={d.brand_name} url={d.brand_logo_url} square to={d.brand_user_id ? `/brands/${d.brand_user_id}` : null} />
        <span className="text-zinc-500 text-xs">×</span>
        <Avatar name={d.creator_name} url={d.creator_avatar_url} to={d.creator_user_id ? `/creators/${d.creator_user_id}` : null} />
        <div className="ml-auto text-right">
          <div className="font-display font-bold text-white text-base leading-none">{gbp(d.amount)}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1 mt-1"><BadgeCheck className="h-3 w-3" /> Paid</div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-sm text-white font-semibold truncate flex items-center gap-1">
          <NameLink to={d.brand_user_id ? `/brands/${d.brand_user_id}` : null} testid="deal-brand-link">{d.brand_name}</NameLink> <BadgeCheck className="h-3 w-3 text-nexus-blue shrink-0" /> <span className="text-zinc-500 font-normal">×</span>
          <NameLink to={d.creator_user_id ? `/creators/${d.creator_user_id}` : null} testid="deal-creator-link">{d.creator_name}</NameLink>
        </div>
        <div className="text-xs text-zinc-400 truncate">
          {d.deal_type}{d.platform && PLATFORM[d.platform] ? ` · ${PLATFORM[d.platform]}` : ""}{d.creator_followers ? ` · ${fmtK(d.creator_followers)} followers` : ""}
        </div>
        <div className="text-[11px] text-zinc-500 truncate mt-0.5">
          {[d.brand_niche, d.brand_city].filter(Boolean).join(" · ")}{d.turnaround_days ? ` · delivered in ${d.turnaround_days} days` : ""}
        </div>
      </div>
      {d.review ? (
        <p className={`text-xs text-zinc-300 leading-relaxed ${compact ? "line-clamp-2" : ""}`}>“{d.review}”</p>
      ) : (
        <p className="text-xs text-zinc-600 italic">Approved without a written review</p>
      )}
      {d.brand_deal_number > 1 && (
        <div className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-nexus-purple/15 border border-nexus-purple/30 text-[10px] uppercase tracking-widest text-purple-200" data-testid="repeat-client-chip">
          Repeat client · deal #{d.brand_deal_number} on Nexus
        </div>
      )}
      <div className="flex items-center justify-between mt-auto pt-1">
        <Stars rating={d.rating} />
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{ago(d.completed_at)}</span>
      </div>
    </article>
  );
}

export function ResultsCounter({ totals, className = "" }) {
  if (!totals?.count) return null;
  const items = [
    { icon: Trophy, v: totals.count, l: "deals completed" },
    { icon: Wallet, v: gbp(totals.paid_gbp), l: "paid to creators" },
    { icon: Star, v: totals.avg_rating ? `${totals.avg_rating.toFixed(1)}★` : "—", l: "average rating" },
    { icon: Building2, v: totals.brands, l: "brands" },
  ];
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`} data-testid="results-counter">
      {items.map((it) => (
        <div key={it.l} className="rounded-2xl bg-white/[0.03] hairline p-4">
          <it.icon className="h-4 w-4 text-nexus-pink mb-2" />
          <div className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight" data-testid={`results-stat-${it.l.replace(/\s+/g, "-")}`}>{it.v}</div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-0.5">{it.l}</div>
        </div>
      ))}
    </div>
  );
}

/** Homepage: counter + auto-scrolling ticker of recent deals. */
export function DealsTicker() {
  const data = useResults(82);
  if (!data?.deals?.length) return null;
  const row = data.deals.slice(0, 24);
  return (
    <section className="px-6 lg:px-10 py-20 border-t border-white/5 overflow-hidden" data-testid="results-ticker-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Results, not promises</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tighter">Deals completed <span className="nexus-text-gradient">on Nexus</span>.</h2>
            <p className="text-zinc-400 mt-3 max-w-xl">Every card is a brief that was funded into escrow, delivered, approved and paid out. Creators kept 100% of their rate.</p>
          </div>
          <Link to="/results" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-nexus-pink transition-colors" data-testid="results-see-all-link">See all results <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <ResultsCounter totals={data.totals} className="mb-8" />
      </div>
      <div className="relative -mx-6 lg:-mx-10">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#050507] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#050507] to-transparent z-10" />
        <div className="flex gap-4 w-max nexus-marquee hover:[animation-play-state:paused]" data-testid="results-ticker">
          {[...row, ...row].map((d, i) => <DealCard key={`${d.id}-${i}`} d={d} compact />)}
        </div>
      </div>
    </section>
  );
}

/** Explore: horizontal strip of recently completed deals. */
export function RecentDealsStrip() {
  const data = useResults(82);
  if (!data?.deals?.length) return null;
  return (
    <section className="rounded-2xl bg-[#0C0C10] hairline p-5 space-y-4" data-testid="recent-deals-strip">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Trophy className="h-3.5 w-3.5 text-nexus-pink" /> Recently completed on Nexus</div>
        <div className="text-xs text-zinc-400"><b className="text-white">{data.totals.count}</b> deals · <b className="text-white">{gbp(data.totals.paid_gbp)}</b> paid out · <Link to="/results" className="text-nexus-blue hover:underline">see all</Link></div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {data.deals.slice(0, 12).map((d) => <DealCard key={d.id} d={d} compact />)}
      </div>
    </section>
  );
}

/** Signup page: compact one-line social proof. */
export function SignupProof({ role }) {
  const data = useResults(82);
  const t = data?.totals;
  if (!t?.count) return null;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hairline mb-6" data-testid="signup-proof">
      <div className="flex -space-x-2 shrink-0">
        {(data.deals || []).slice(0, 4).map((d) => <div key={d.id} className={`h-7 w-7 rounded-full ring-2 ring-[#0C0C10] bg-gradient-to-br ${hue(d.creator_name)} flex items-center justify-center text-[10px] font-bold text-white`}>{d.creator_name[0]}</div>)}
      </div>
      <div className="text-xs text-zinc-300 leading-snug">
        <b className="text-white">{t.count} deals completed</b> · <b className="text-white">{gbp(t.paid_gbp)}</b> paid to creators · {t.avg_rating?.toFixed(1)}★ average.
        {role === "brand" ? " Escrow-protected — you only pay when you approve." : " Creators keep 100% of their rate."}
      </div>
    </div>
  );
}
